import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/platform/StatusBadge';
import type { PersonSummary } from '@/lib/api/people';
import { formatSourceLabel } from '@/lib/sourceLabel';

interface PeopleTableProps {
  people: PersonSummary[];
  isLoading: boolean;
  offset: number;
}

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const compactText = (value: string | null | undefined, fallback = '—') =>
  value && value.trim() ? value : fallback;

function QualificationBadge({ person }: { person: PersonSummary }) {
  const status = person.updated_qualification_status || person.qualification_status;
  if (status === 'qualified') {
    return (
      <StatusBadge tone="success">
        Qualified
      </StatusBadge>
    );
  }
  if (status === 'unqualified') {
    return (
      <StatusBadge tone="danger">
        Unqualified
      </StatusBadge>
    );
  }
  return (
    <StatusBadge tone="pending">
      Unknown
    </StatusBadge>
  );
}

function IntentBadge({ intent }: { intent: string | null | undefined }) {
  if (intent === 'high') {
    return (
      <StatusBadge tone="success">
        High
      </StatusBadge>
    );
  }
  if (intent === 'medium') {
    return (
      <StatusBadge tone="warning">
        Medium
      </StatusBadge>
    );
  }
  if (intent === 'low') {
    return (
      <StatusBadge tone="neutral">
        Low
      </StatusBadge>
    );
  }
  return <span className="text-muted-foreground text-[13px]">—</span>;
}

const PeopleTable: React.FC<PeopleTableProps> = ({ people, isLoading }) => {
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (people.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <p className="text-sm">No people yet.</p>
      </div>
    );
  }

  return (
    <Table aria-label="People analytics table">
      <TableCaption className="sr-only">
        List of people with engagement, qualification, and attribution details.
      </TableCaption>
      <TableHeader className="bg-transparent">
        <TableRow className="hover:bg-transparent">
          <TableHead className="text-[12px] font-normal text-muted-foreground">Person</TableHead>
          <TableHead className="whitespace-nowrap text-[12px] font-normal text-muted-foreground">Last Seen</TableHead>
          <TableHead className="text-center text-[12px] font-normal text-muted-foreground">Sessions</TableHead>
          <TableHead className="hidden text-[12px] font-normal text-muted-foreground lg:table-cell">Source</TableHead>
          <TableHead className="hidden text-[12px] font-normal text-muted-foreground xl:table-cell">Time</TableHead>
          <TableHead className="hidden text-[12px] font-normal text-muted-foreground 2xl:table-cell">Location</TableHead>
          <TableHead className="hidden text-[12px] font-normal text-muted-foreground md:table-cell">Qualification</TableHead>
          <TableHead className="hidden text-[12px] font-normal text-muted-foreground md:table-cell">Intent</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {people.map((person) => (
          <TableRow
            key={person.id}
            className="h-10 cursor-pointer border-border/70 hover:bg-accent/50"
            onClick={() => navigate(`/people/${encodeURIComponent(person.email)}`)}
          >
            <TableCell className="min-w-[200px]">
              <div className="flex min-w-0 items-start gap-3">
                <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                  {(person.name || person.email || 'U').charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-normal text-foreground">{person.email}</div>
                  {person.business && (
                    <span className="sr-only">Company {person.business}</span>
                  )}
                  <div className="mt-1 flex flex-wrap gap-1 md:hidden">
                    <QualificationBadge person={person} />
                    <IntentBadge intent={person.buying_intent} />
                  </div>
                </div>
              </div>
            </TableCell>
            <TableCell className="whitespace-nowrap text-[13px] text-foreground">
              {formatDate(person.last_seen_at)}
            </TableCell>
            <TableCell className="text-center text-[13px] tabular-nums">
              {person.total_sessions}
            </TableCell>
            <TableCell className="hidden text-[13px] text-foreground lg:table-cell">
              {formatSourceLabel(person.last_source)}
            </TableCell>
            <TableCell className="hidden whitespace-nowrap text-[13px] text-foreground tabular-nums xl:table-cell">
              {formatDuration(person.total_time_spent_seconds)}
            </TableCell>
            <TableCell className="hidden max-w-[160px] truncate text-[13px] text-foreground 2xl:table-cell">
              {compactText(person.last_location)}
            </TableCell>
            <TableCell className="hidden md:table-cell">
              <QualificationBadge person={person} />
            </TableCell>
            <TableCell className="hidden md:table-cell">
              <IntentBadge intent={person.buying_intent} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default PeopleTable;
